import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import type { components } from '@/api/schema'

import type { PublicPlans, UsageDetail, UsageSummary, UsageWindowOption } from './types'

/** `int32`/`double` chegam como `number | string` (precisao). Saldos e valores cabem no intervalo seguro. */
function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : Number(value)
}

function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

function mapSummary(dto: components['schemas']['UsageSummaryView']): UsageSummary {
  return {
    state: dto.state,
    enforcement: dto.enforcement,
    available: toNumber(dto.available),
    pausedForCredits: dto.pausedForCredits,
    lowBalance: dto.lowBalance,
    trialEndsAt: dto.trialEndsAt,
    currentPeriodEnd: dto.currentPeriodEnd,
    openInvoice:
      dto.openInvoice === null
        ? null
        : {
            id: dto.openInvoice.id,
            number: dto.openInvoice.number,
            amount: toNumber(dto.openInvoice.amount),
            dueDate: dto.openInvoice.dueDate,
            paymentUrl: dto.openInvoice.paymentUrl,
          },
    planCode: dto.planCode,
  }
}

/** Resumo de uso (qualquer membro): estado, saldo, "IA pausada", saldo baixo. */
export async function getUsageSummary(tenantId: string): Promise<UsageSummary> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/usage/summary', {
    params: { path: { tenantId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return mapSummary(result.data)
}

/** Detalhe de uso (`billing.view`). `null` = sem permissao (403), mostrado como estado honesto. */
export async function getUsage(
  tenantId: string,
  days: UsageWindowOption,
): Promise<UsageDetail | null> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/usage', {
    params: { path: { tenantId }, query: { days } },
  })

  if (result.response.status === 403) {
    return null
  }

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  const dto = result.data
  return {
    summary: mapSummary(dto.summary),
    buckets: dto.buckets.map((b) => ({
      kind: b.kind,
      credits: toNumber(b.credits),
      consumed: toNumber(b.consumed),
      expiresAt: b.expiresAt,
    })),
    byKind: dto.byKind.map((k) => ({ kind: k.kind, credits: toNumber(k.credits) })),
    daily: dto.daily.map((d) => ({ date: d.date, credits: toNumber(d.credits) })),
    windowDays: toNumber(dto.windowDays),
  }
}

/** Catalogo publico (anonimo): teste gratis e planos. */
export async function getPublicPlans(): Promise<PublicPlans> {
  const result = await apiClient.GET('/api/v1/plans')

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  const dto = result.data
  return {
    currency: dto.currency,
    trial: { credits: toNumber(dto.trial.credits), days: toNumber(dto.trial.days) },
    plans: dto.plans.map((p) => ({
      code: p.code,
      monthlyPrice: p.monthlyPrice === null ? null : toNumber(p.monthlyPrice),
      selfServe: p.selfServe,
    })),
  }
}
